package org.matthewo.pages.team.tabs

import emotion.react.css
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import js.coroutines.awaitCancellation
import js.objects.buildReadonlyRecord
import kotlinx.coroutines.launch
import org.matthewo.*
import org.matthewo.api.*
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.js.toFixed
import org.matthewo.pages.event.EVENT_CODE_PARAM
import org.matthewo.pages.event.tabs.toEventMatchResult
import org.matthewo.pages.team.TEAM_PARAM
import org.matthewo.recharts.cartesian.CartesianGrid
import org.matthewo.recharts.cartesian.Line
import org.matthewo.recharts.cartesian.XAxis
import org.matthewo.recharts.cartesian.YAxis
import org.matthewo.recharts.chart.LineChart
import org.matthewo.recharts.component.Tooltip
import org.matthewo.recharts.container.Legend
import org.matthewo.recharts.container.ResponsiveContainer
import org.matthewo.recharts.type.AxisType
import org.matthewo.recharts.type.ChartMargin
import org.matthewo.recharts.type.LayoutDirection
import org.matthewo.recharts.type.LineType
import org.matthewo.router.contains
import org.matthewo.routes.API
import org.matthewo.styles.*
import react.FC
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.useEffect
import react.useMemo
import react.useState
import tanstack.react.router.useNavigate
import tanstack.react.router.useParams
import tanstack.react.router.useSearch
import web.cssom.*

private val GAME_SPECIFIC_CONFIGS = mapOf(
    -1 to [
        ContributionSeries("NP Total", TOTAL_COLOR.chart, EventTeamMatch::npTotal),
        ContributionSeries("Auto", AUTO_COLOR.chart, EventTeamMatch::auto),
        ContributionSeries("Teleop", TELEOP_COLOR.chart, EventTeamMatch::teleop),
        ContributionSeries("Endgame", ENDGAME_COLOR.chart, EventTeamMatch::endgame),
        ContributionSeries("Penalty", NamedColor.white, EventTeamMatch::penalty),
    ],
    2021 to [
        ContributionSeries("Alliance", AUTO_COLOR.chart) { (it.gameSpecific as? EventTeamMatchFreightFrenzy)?.alliance ?: 0 },
        ContributionSeries("Shared", TELEOP_COLOR.chart) { (it.gameSpecific as? EventTeamMatchFreightFrenzy)?.shared ?: 0 },
        ContributionSeries("Carousel", ENDGAME_COLOR.chart) { (it.gameSpecific as? EventTeamMatchFreightFrenzy)?.carousel ?: 0 },
    ],
    102021 to [
        ContributionSeries("Alliance", AUTO_COLOR.chart) { (it.gameSpecific as? EventTeamMatchFreightFrenzyCRI)?.alliance ?: 0 },
        ContributionSeries("Shared", TELEOP_COLOR.chart) { (it.gameSpecific as? EventTeamMatchFreightFrenzyCRI)?.shared ?: 0 },
        ContributionSeries("Coop", ENDGAME_COLOR.chart) { (it.gameSpecific as? EventTeamMatchFreightFrenzyCRI)?.coop ?: 0 },
    ],
    2024 to [
        ContributionSeries("Specimen", AUTO_COLOR.chart) { (it.gameSpecific as? EventTeamMatchIntoTheDeep)?.specimen ?: 0 },
        ContributionSeries("Sample", TELEOP_COLOR.chart) { (it.gameSpecific as? EventTeamMatchIntoTheDeep)?.sample ?: 0 },
        ContributionSeries("Navigation", ENDGAME_COLOR.chart) { (it.gameSpecific as? EventTeamMatchIntoTheDeep)?.navigation ?: 0 },
    ],
    102024 to [
        ContributionSeries("Specimen", AUTO_COLOR.chart) { (it.gameSpecific as? EventTeamMatchIntoTheDeepCRI)?.specimen ?: 0 },
        ContributionSeries("Sample", TELEOP_COLOR.chart) { (it.gameSpecific as? EventTeamMatchIntoTheDeepCRI)?.sample ?: 0 },
        ContributionSeries("Navigation", ENDGAME_COLOR.chart) { (it.gameSpecific as? EventTeamMatchIntoTheDeepCRI)?.navigation ?: 0 },
    ],
)

private data class ContributionSeries(val title: String, val color: Color, val stat: (EventTeamMatch) -> Number)

val TeamContributionPlot = FC("TeamContributionPlot") {
    val coroutineScope = useCoroutineScope()
    val navigate = useNavigate()
    val pathParams = useParams()
    val searchParams = useSearch()

    val year = pathParams[YEAR_PARAM]?.toIntOrNull()
    val code = pathParams[EVENT_CODE_PARAM]

    var data by useState<TeamData<List<EventTeamMatch>>>()

    useLoading(null == data)
    useTitleHeader(data) { data?.run { "$number - $name" } ?: "" }
    val matches = useMemo(data) {
        data?.data?.toTypedArray() ?: []
    }

    var displaySeriesLevel by useState(mapOf<Schedule.Match.Level, (EventTeamMatch) -> String>())
    useEffect(matches) {
        displaySeriesLevel = matches.groupBy { it.level }.mapValues { (level, matches) ->
            val showSeries = matches.distinctBy { it.series }.count() > 1
            val showNumber = matches.distinctBy { it.number }.count() > 1
            when {
                showSeries && showNumber -> { match: EventTeamMatch -> "${match.series}-${match.number}" }
                showSeries -> { match: EventTeamMatch -> "${match.series}" }
                showNumber -> { match: EventTeamMatch -> "${match.number}" }
                matches.first().series > matches.first().number -> { match: EventTeamMatch -> "${match.series}" }
                else -> { match: EventTeamMatch -> "${match.number}" }
            }
        }
    }
    useEffect(pathParams) {
        val year = year ?: return@useEffect
        val teamNumber = pathParams[TEAM_PARAM]?.toIntOrNull() ?: return@useEffect
        val job = coroutineScope.launch {
            val contribution = API.Team.Contribution(year, teamNumber)
            val response =
                if (null == code) client.get(contribution)
                else client.get(API.Team.Contribution.Event(contribution, code))
            data = response.body()
        }
        awaitCancellation { job.cancel() }
    }

    val gameSpecific = useMemo(searchParams) { GAME_SPECIFIC_SEARCH in searchParams && year in GAME_SPECIFIC_CONFIGS }
    val stats = GAME_SPECIFIC_CONFIGS[if (gameSpecific) year else -1]!!

    val EventTeamMatchesTooltip = FC("EventTeamMatchesTooltip") { propsTooltip: dynamic ->
        if (!propsTooltip["active"].unsafeCast<Boolean>()) return@FC
        val payload = propsTooltip["payload"][0]
        if (undefined == payload) return@FC
        val instance = payload["payload"].unsafeCast<EventTeamMatch>()
        div {
            css {
                opacity = number(0.9)
                background = BACKGROUND_COLOR
                border = Border(2.px, LineStyle.solid, TITLE_FONT_COLOR)
                borderRadius = 4.px
                color = NamedColor.white
                padding = array(0.0.em, 0.5.em)

                ".label" {
                    fontWeight = FontWeight.bold
                }
            }
            p {
                className = ClassName("label")
                b {
                    +instance.code
                }
            }
            p {
                className = ClassName("label")
                b {
                    +"${instance.level.title}: ${displaySeriesLevel.getOrElse(instance.level) { { "" } }(instance)}"
                }
            }
            stats.forEach { stat ->
                p {
                    className = ClassName("intro")
                    +"${stat.title}: ${stat.stat(instance).toDouble().toFixed(1)}"
                }
            }
        }
    }

    div {
        css {
            width = 100.pct
            height = 100.pct
            fontSize = 1.em
            overflow = Overflow.hidden
        }
        ResponsiveContainer {
            width = 100.pct
            height = 100.pct
            LineChart {
                margin = ChartMargin(0, 15, 15, 0)

                onClick = { props ->
                    val payload = props["activePayload"]
                    if (undefined != payload && null != year) {
                        val instance = payload[0]["payload"].unsafeCast<EventTeamMatch>()
                        navigate {
                            toEventMatchResult(eventTeamMatch = instance)
                        }
                    }
                }
                this.data = matches
                CartesianGrid {
                    strokeDasharray = "3 3"
                    vertical = false
                }
                XAxis {
                    type = AxisType.category
                    dataKey = { instance: EventTeamMatch ->
                        "${instance.level.abbreviation}${displaySeriesLevel.getOrElse(instance.level) { { "" } }(instance)}"
                    }
                }
                YAxis()
                Legend {
                    layout = LayoutDirection.horizontal
                    onClick = {
                        navigate {
                            search = buildReadonlyRecord {
                                if (gameSpecific || year !in GAME_SPECIFIC_CONFIGS) this[GAME_SPECIFIC_SEARCH] = Unit
                            }
                            replace = true
                        }
                    }
                }
                stats.forEach { stat ->
                    Line {
                        type = LineType.monotone
                        dataKey = { instance: EventTeamMatch ->
                            stat.stat(instance).toDouble()
                        }
                        stroke = stat.color.toString()
                        strokeWidth = 2
                        name = stat.title
                    }
                }
                Tooltip {
                    content = EventTeamMatchesTooltip
                }
            }
        }
    }
}
